import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { IdCard, ArrowRight } from "lucide-react";

export default function ReferrerRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.name || "",
    birth_date: "",
    nif: "",
    address: "",
    postal_code: "",
    locality: "",
    phone: "",
    email: user?.email || "",
    payment_method: "Cartão Dá",
    rgpd_consent: false,
    terms_accepted: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/referrers/me").then(({ data }) => {
      if (data) navigate("/onboarding/protocol", { replace: true });
    });
  }, [navigate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd_consent || !form.terms_accepted) {
      toast.error("Precisa de aceitar o RGPD e os Termos.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/referrers/register", form);
      toast.success("Perfil criado. Vamos ao protocolo.");
      navigate("/onboarding/protocol");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="max-w-3xl">
      <div className="eyebrow">Passo 1 de 2</div>
      <h1 className="font-heading text-4xl mt-2 flex items-center gap-3"><IdCard className="w-8 h-8 text-[var(--forest)]"/> Complete o seu perfil</h1>
      <p className="text-[var(--ink-soft)] mt-2">Os seus dados são utilizados apenas para gestão de prémios e comunicações do programa.</p>

      <form onSubmit={submit} className="mt-8 card-surface p-8 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Nome completo</label>
            <input required value={form.full_name} onChange={update("full_name")} className={`mt-1.5 ${inputCls}`} data-testid="ref-full-name-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">Data de nascimento</label>
            <input required type="date" value={form.birth_date} onChange={update("birth_date")} className={`mt-1.5 ${inputCls}`} data-testid="ref-birth-date-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">NIF</label>
            <input required value={form.nif} onChange={update("nif")} className={`mt-1.5 ${inputCls}`} data-testid="ref-nif-input" placeholder="123456789"/>
          </div>
          <div>
            <label className="text-sm font-medium">Telefone</label>
            <input required value={form.phone} onChange={update("phone")} className={`mt-1.5 ${inputCls}`} data-testid="ref-phone-input" placeholder="+351 …"/>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Morada</label>
            <input required value={form.address} onChange={update("address")} className={`mt-1.5 ${inputCls}`} data-testid="ref-address-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">Código Postal</label>
            <input required value={form.postal_code} onChange={update("postal_code")} className={`mt-1.5 ${inputCls}`} data-testid="ref-postal-input" placeholder="0000-000"/>
          </div>
          <div>
            <label className="text-sm font-medium">Localidade</label>
            <input required value={form.locality} onChange={update("locality")} className={`mt-1.5 ${inputCls}`} data-testid="ref-locality-input"/>
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input required type="email" value={form.email} onChange={update("email")} className={`mt-1.5 ${inputCls}`} data-testid="ref-email-input"/>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Método de pagamento</label>
            <div className="mt-1.5 flex items-center gap-2 p-3 rounded-xl bg-[var(--muted)] border border-[var(--border)]">
              <div className="chip chip-terra">Cartão Dá</div>
              <span className="text-xs text-[var(--ink-soft)]">Pré-definido pelo programa</span>
            </div>
          </div>
        </div>

        <label className="flex items-start gap-3 pt-2">
          <input type="checkbox" checked={form.rgpd_consent} onChange={update("rgpd_consent")} className="mt-1" data-testid="ref-rgpd-checkbox"/>
          <span className="text-sm text-[var(--ink-soft)]">Consinto que a CARING trate os meus dados pessoais para fins de gestão do Programa de Referências, conforme o RGPD.</span>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" checked={form.terms_accepted} onChange={update("terms_accepted")} className="mt-1" data-testid="ref-terms-checkbox"/>
          <span className="text-sm text-[var(--ink-soft)]">Aceito os Termos e Condições do programa. Sei que não existe qualquer vínculo laboral com a CARING.</span>
        </label>

        <button disabled={busy} className="pill-btn pill-primary w-full justify-center" data-testid="ref-submit-btn">
          {busy ? "A guardar…" : "Continuar para o protocolo"} <ArrowRight className="w-4 h-4"/>
        </button>
      </form>
    </div>
  );
}
